"use client";

import { BookOpen, Swords, GitMerge, Eye, Layers, Sparkles, Compass, Users, FileText, Heart } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { storyFrameworkBuilder as data } from "@/lib/data/capabilities-narrative-engineering";

const libraryIcons = [BookOpen, Swords, GitMerge, Eye, Layers, Sparkles];
const selectionIcons = [Compass, Users, Globe, FileText];
const customizationIcons = [Users, Heart, Sparkles, Eye];

const accents = [
  "var(--color-crimson)",
  "var(--color-purple)",
  "var(--color-cyan)",
  "var(--color-emerald)",
  "var(--color-amber)",
  "var(--color-violet)",
] as const;

const selectionAccent = "var(--color-amber)";
const customizationAccent = "var(--color-emerald)";

import { Globe } from "lucide-react";

export function StoryFrameworkBuilderSection() {
  return (
    <Section id="story-framework-builder" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/3 right-1/3 h-80 w-80 rounded-full bg-[var(--color-purple)] opacity-15 blur-[120px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-purple)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-12">
            <div className="eyebrow-tiny text-[var(--color-crimson)] mb-4">Framework Library</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.library.map((l, i) => {
                const Icon = libraryIcons[i % libraryIcons.length]!;
                const accent = accents[i % accents.length]!;
                return (
                  <div
                    key={l.name}
                    className="glass rounded-2xl p-5"
                    style={{
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)`,
                    }}
                  >
                    <Icon className="h-5 w-5 mb-2" style={{ color: accent }} />
                    <h3 className="text-sm font-bold">{l.name}</h3>
                    <p className="mt-1.5 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{l.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          <Reveal delay={200}>
            <div
              className="glass rounded-2xl p-6 h-full"
              style={{
                boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${selectionAccent} 14%, transparent)`,
              }}
            >
              <div className="eyebrow-tiny mb-4" style={{ color: selectionAccent }}>
                Framework Selection Intelligence
              </div>
              <div className="space-y-4">
                {data.selectionCriteria.map((s, i) => {
                  const Icon = selectionIcons[i % selectionIcons.length]!;
                  return (
                    <div key={s.title} className="flex items-start gap-3">
                      <span
                        className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                        style={{
                          background: `color-mix(in oklch, ${selectionAccent} 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${selectionAccent} 35%, transparent)`,
                        }}
                      >
                        <Icon className="h-4 w-4" style={{ color: selectionAccent }} />
                      </span>
                      <div>
                        <div className="font-semibold text-sm">{s.title}</div>
                        <div className="text-xs text-[rgb(var(--fg-muted))] mt-0.5 leading-relaxed">{s.body}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>

          <Reveal delay={280}>
            <div
              className="glass rounded-2xl p-6 h-full"
              style={{
                boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${customizationAccent} 14%, transparent)`,
              }}
            >
              <div className="eyebrow-tiny mb-4" style={{ color: customizationAccent }}>
                Framework Customization Engine
              </div>
              <div className="space-y-4">
                {data.customization.map((c, i) => {
                  const Icon = customizationIcons[i % customizationIcons.length]!;
                  return (
                    <div key={c.title} className="flex items-start gap-3">
                      <span
                        className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                        style={{
                          background: `color-mix(in oklch, ${customizationAccent} 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${customizationAccent} 35%, transparent)`,
                        }}
                      >
                        <Icon className="h-4 w-4" style={{ color: customizationAccent }} />
                      </span>
                      <div>
                        <div className="font-semibold text-sm">{c.title}</div>
                        <div className="text-xs text-[rgb(var(--fg-muted))] mt-0.5 leading-relaxed">{c.body}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </Section>
  );
}
